import { FC, useCallback, useState } from "react";
import { Drawer } from "@mui/material";
import { useEffectOnceWhen } from "rooks";
import Stack from "@mui/material/Stack";
import { AutomationsFoundResourcesDrawerHeader } from "./components/AutomationsFoundResourcesDrawerHeader";
import { PreviewDataGridSkeleton } from "@/components/pages/automations/common/components/found-resources/components/helper-components/PreviewDataGridSkeleton";
import { useAppDispatch } from "@/store/hooks";
import {
  ProviderType,
  ResourceFiltersWithAction,
  ScheduleStatus,
} from "@/services/cloudchipr.api";
import { AutomationsFoundResourcesCard } from "@/components/pages/automations/common/components/found-resources/components/AutomationsFoundResourcesCard";
import { getResourcesVisibilitiesByAccountIdsAndProviderThunk } from "@/store/live-usage-mgmt/thunks/resources/resource-visibility/getResourcesVisibilitiesByAccountIdsAndProviderThunk";
import { AutomationsFoundResourcesDrawerAppBar } from "@/components/pages/automations/common/components/found-resources/components/app-bar/AutomationsFoundResourcesDrawerAppBar";

interface AutomationsFoundResourcesDrawerProps {
  open: boolean;
  name?: string;
  onClose(): void;
  regions: string[];
  editingUrl?: string;
  accountIds: string[];
  status?: ScheduleStatus;
  gracePeriodEnabled: boolean;
  scheduleAction: string | null;
  provider: ProviderType | null;
  filters: ResourceFiltersWithAction[];

  // appBarProps
  gracePeriodValue?: number;
  nextRuns?: { title: string; date: string; utc?: string }[];
  frequency?: { title: string; label: string; tooltip: string }[];
}

export const AutomationsFoundResourcesDrawer: FC<
  AutomationsFoundResourcesDrawerProps
> = ({
  name,
  status,
  onClose,
  open,
  accountIds,
  filters,
  regions,
  editingUrl,
  scheduleAction,
  gracePeriodEnabled,
  provider,
  gracePeriodValue,
  nextRuns,
  frequency,
}) => {
  const dispatch = useAppDispatch();
  const [renderBody, setRenderBody] = useState(false);

  const onTransitionEnd = useCallback(() => {
    !renderBody && open && setRenderBody(true);
  }, [renderBody, open]);

  useEffectOnceWhen(
    () => {
      if (provider) {
        dispatch(
          getResourcesVisibilitiesByAccountIdsAndProviderThunk({
            provider,
            accountIds,
          }),
        );
      }
    },
    !!(accountIds?.length && provider),
  );

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={onClose}
      onTransitionEnd={onTransitionEnd}
      PaperProps={{ sx: { width: "80vw", height: "100%", overflow: "hidden" } }}
    >
      <AutomationsFoundResourcesDrawerHeader
        editingUrl={editingUrl}
        status={status}
        title={name ?? ""}
        onClose={onClose}
      />

      <AutomationsFoundResourcesDrawerAppBar
        filters={filters}
        nextRuns={nextRuns}
        frequency={frequency}
        gracePeriodValue={gracePeriodValue}
      />

      <Stack px={3} py={2} spacing={2} height="100%" overflow="auto">
        {renderBody ? (
          open &&
          filters.map((filter) => {
            return (
              <AutomationsFoundResourcesCard
                regions={regions}
                filters={filter}
                accountIds={accountIds}
                key={filter.filter.type}
                singleGrid={filters.length === 1}
                scheduleAction={scheduleAction}
                gracePeriodEnabled={gracePeriodEnabled}
              />
            );
          })
        ) : (
          <PreviewDataGridSkeleton />
        )}
      </Stack>
    </Drawer>
  );
};
